import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BackgroundLayer from "../components/BackgroundLayer";
import { ScanLine, ShieldCheck, TrendingUp, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    icon: ScanLine,
    title: "Identify Any Plant Instantly.",
    sub: "Snap a photo and discover 400,000+ species.",
  },
  {
    icon: ShieldCheck,
    title: "Diagnose & Heal Your Plants.",
    sub: "AI detects diseases and suggests treatments.",
  },
  {
    icon: TrendingUp,
    title: "Track Growth Like a Pro.",
    sub: "Weekly AI reports monitor your plant's health.",
  },
];

export default function OnboardingScreen() {
  const [i, setI] = useState(0);
  const navigate = useNavigate();

  const finish = () => {
    localStorage.setItem("cf_onboarded", "1");
    navigate("/login", { replace: true });
  };

  const next = () => {
    if (i < SLIDES.length - 1) setI(i + 1);
    else finish();
  };

  const isLast = i === SLIDES.length - 1;
  const S = SLIDES[i];
  const Icon = S.icon;

  return (
    <div className="min-h-screen flex flex-col relative">
      <BackgroundLayer section="welcome" />

      <header className="flex items-center justify-end px-6 py-5">
        <button
          onClick={finish}
          data-testid="onboarding-skip"
          style={{ fontSize: 14, color: "rgba(255,255,255,0.55)" }}
          className="hover:text-white transition-colors"
        >
          Skip
        </button>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <div
          key={i}
          data-testid={`onboarding-slide-${i}`}
          className="onboarding-card fade-up"
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 340,
            padding: "44px 28px 36px",
            background: "rgba(10, 10, 10, 0.78)",
            backdropFilter: "blur(24px) saturate(160%)",
            WebkitBackdropFilter: "blur(24px) saturate(160%)",
            borderRadius: 20,
            border: "1px solid rgba(76, 175, 80, 0.2)",
            boxShadow:
              "0 0 18px rgba(76,175,80,0.35), 0 12px 40px rgba(0,0,0,0.55)",
            overflow: "visible",
          }}
        >
          {/* Bright animated top accent bar — sits on top of the card, fully visible */}
          <span
            aria-hidden
            data-testid="onboarding-top-accent"
            className="onboarding-top-bar"
            style={{
              position: "absolute",
              top: 0,
              left: 16,
              right: 16,
              height: 3,
              background: "linear-gradient(90deg, transparent, #4CAF50 20%, #4CAF50 80%, transparent)",
              borderRadius: 999,
              boxShadow: "0 0 18px rgba(76,175,80,0.95), 0 0 36px rgba(76,175,80,0.6)",
            }}
          />

          {/* Neon icon, 64px stroke-1.5 */}
          <div
            className="flex items-center justify-center"
            style={{ marginBottom: 28 }}
          >
            <Icon
              size={64}
              strokeWidth={1.5}
              color="#4CAF50"
              style={{
                filter: "drop-shadow(0 0 10px rgba(76,175,80,0.7))",
              }}
            />
          </div>

          <h2
            className="text-center"
            style={{
              fontSize: 22,
              color: "#fff",
              fontWeight: 700,
              lineHeight: 1.25,
              marginBottom: 12,
              textShadow: "0 0 18px rgba(76,175,80,0.25)",
            }}
          >
            {S.title}
          </h2>

          <p
            className="text-center"
            style={{
              fontSize: 15,
              color: "rgba(255,255,255,0.72)",
              lineHeight: 1.5,
            }}
          >
            {S.sub}
          </p>
        </div>
      </div>

      <footer className="px-6 pb-10 flex flex-col items-center gap-5">
        {!isLast ? (
          <>
            <div
              className="flex items-center gap-2"
              data-testid="onboarding-dots"
            >
              {SLIDES.map((_, idx) => (
                <span
                  key={idx}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background:
                      idx === i ? "var(--accent-green)" : "rgba(255,255,255,0.2)",
                    boxShadow: idx === i ? "var(--glow-green)" : "none",
                    transition: "background 220ms, box-shadow 220ms",
                  }}
                />
              ))}
            </div>
            <button
              onClick={next}
              data-testid="onboarding-next"
              className="btn-outline inline-flex items-center gap-2"
              style={{ minWidth: 180, justifyContent: "center" }}
            >
              Next <ChevronRight size={16} />
            </button>
          </>
        ) : (
          <button
            onClick={finish}
            data-testid="onboarding-get-started"
            className="btn-primary inline-flex items-center gap-2"
            style={{ minWidth: 220, justifyContent: "center" }}
          >
            Get Started <ChevronRight size={16} />
          </button>
        )}
      </footer>
    </div>
  );
}
